<template>
  <div ref="wrapperRef" class="m-svg-wrapper">
    <div
      ref="containerRef"
      class="m-svg-container"
      :class="svgClasses"
      :style="svgStyle"
      v-html="svgMarkup"
      @click="handleClick"
      @mouseover="handleHover"
    />
    <div v-if="hint" class="m-svg-hint">{{ hint }}</div>
    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />
  </div>
</template>

<script setup lang="ts">
import DOMPurify from 'dompurify'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import { nextTick, computed, onMounted, ref, watch } from 'vue'
import type { FileItem } from '@/common/types/fileItem'
import { fetchPlatformAttachmentDownload } from '@/common/utils/platformDownload'
import { SVGContainer } from '@/common/utils/svgContainer'
import { getScalarFromFields } from '@/common/utils/fileAttachmentsUniData'

interface Props {
  modelValue?: string
  fileFields?: Record<string, unknown>
  elementId?: number | string
  sessionId?: string
  platform?: string
  width?: string
  height?: string
  hint?: string
  disabled?: boolean
  errorMessages?: string | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  fileFields: () => ({}),
  elementId: undefined,
  sessionId: '',
  platform: '',
  width: '100%',
  height: 'auto',
  hint: '',
  disabled: false,
  errorMessages: () => [],
})

const emit = defineEmits<{
  click: [payload: { event: MouseEvent; element: any; data: Record<string, unknown> }]
  hover: [payload: { event: MouseEvent; element: any; data: Record<string, unknown> }]
  ready: []
}>()

const wrapperRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const svgMarkup = ref('')
const svgContainer = ref<SVGContainer | null>(null)
let loadSequence = 0

const normalizedErrors = computed(() =>
  Array.isArray(props.errorMessages) ? props.errorMessages : [props.errorMessages],
)
const svgClasses = computed(() => ['m-svg', { 'm-svg--disabled': props.disabled }])
const svgStyle = computed(() => ({ width: props.width, height: props.height }))

function sanitizeSvg(value: string): string {
  if (!value) return ''
  return DOMPurify.sanitize(value, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: [
      'svg', 'a', 'path', 'circle', 'ellipse', 'rect', 'line', 'polyline', 'polygon', 'g',
      'defs', 'use', 'symbol', 'marker', 'pattern', 'image', 'text', 'tspan', 'title',
      'animate', 'animateTransform', 'animateMotion', 'style',
    ],
    ADD_ATTR: [
      'id', 'viewBox', 'xmlns', 'xmlns:xlink', 'xlink:href', 'fill', 'fill-opacity', 'stroke',
      'stroke-width', 'stroke-opacity', 'stroke-dasharray', 'stroke-linecap', 'stroke-linejoin',
      'd', 'cx', 'cy', 'r', 'rx', 'ry', 'x', 'y', 'x1', 'x2', 'y1', 'y2', 'width', 'height',
      'transform', 'opacity', 'display', 'visibility', 'preserveAspectRatio', 'class', 'style',
      'begin', 'dur', 'end', 'repeatCount', 'from', 'to', 'by', 'values', 'attributeName',
      'attributeType', 'pointer-events', 'data-name',
    ],
  })
}

function fieldValue(name: string): unknown {
  return getScalarFromFields(props.fileFields, name)
}

function buildFileItem(): FileItem {
  const fields: Record<string, { type: string; value: unknown }> = {}
  const code = fieldValue('code') ?? fieldValue('_id')
  const version = fieldValue('version') ?? fieldValue('_versionId')
  const hash = fieldValue('hash')
  const fileName = fieldValue('fileName') ?? fieldValue('name')
  if (code != null && code !== '') fields.code = { type: 'Long', value: code }
  if (version != null && version !== '') fields.version = { type: 'Long', value: version }
  if (hash != null && hash !== '') fields.hash = { type: 'String', value: hash }
  if (fileName != null && fileName !== '') fields.fileName = { type: 'String', value: fileName }
  return { fields } as FileItem
}

async function loadSvg(): Promise<void> {
  const sequence = ++loadSequence
  const inlineValue = String(props.modelValue ?? '').trim()
  svgMarkup.value = ''
  svgContainer.value = null
  if (inlineValue) {
    svgMarkup.value = sanitizeSvg(inlineValue)
  } else {
    const sessionId = String(props.sessionId ?? '').trim()
    if (!sessionId || props.elementId == null) return
    try {
      const result = await fetchPlatformAttachmentDownload({
        elementId: props.elementId,
        sessionId,
        type: props.platform,
        item: buildFileItem(),
      })
      if (!result || sequence !== loadSequence) return
      const markup = await result.blob.text()
      if (sequence !== loadSequence) return
      svgMarkup.value = sanitizeSvg(markup)
    } catch (error) {
      console.error('[SVG] Download failed', error)
      return
    }
  }

  await nextTick()
  if (sequence !== loadSequence) return
  const root = containerRef.value?.querySelector('svg') as SVGSVGElement | null
  if (!root) return
  svgContainer.value = new SVGContainer(root)
  emit('ready')
}

function resolveElement(event: MouseEvent): any {
  const target = event.target instanceof Element ? event.target : null
  const element = target?.closest?.('[id]')
  if (!element || !containerRef.value?.contains(element)) return null
  return element
}

function elementData(element: any): Record<string, unknown> {
  const data: Record<string, unknown> = {
    id: element.id,
    _type: element.tagName,
    elementType: element.tagName,
  }
  for (const attribute of Array.from(element.attributes ?? []) as Attr[]) {
    if (attribute.name !== 'id') data[attribute.name] = attribute.value
  }
  const dataAdapter = {
    setField: (name: string, value: unknown) => { data[name] = value },
    setFieldAsObject: (name: string, value: unknown) => { data[name] = value },
  }
  svgContainer.value?.getObjectInfoForElementId(element.id, dataAdapter)
  svgContainer.value?.getValuesForElementId(element.id, dataAdapter)
  data.vaActionsList = svgContainer.value?.getActionsForElementId(element.id) ?? null
  return data
}

function handleClick(event: MouseEvent): void {
  if (props.disabled) return
  const element = resolveElement(event)
  if (element) emit('click', { event, element, data: elementData(element) })
}

function handleHover(event: MouseEvent): void {
  if (props.disabled) return
  const element = resolveElement(event)
  if (element) emit('hover', { event, element, data: elementData(element) })
}

watch([
  () => props.modelValue,
  () => props.elementId,
  () => props.sessionId,
  () => props.platform,
  () => fieldValue('code') ?? fieldValue('_id'),
  () => fieldValue('version') ?? fieldValue('_versionId'),
  () => fieldValue('hash'),
  () => fieldValue('fileName') ?? fieldValue('name'),
], loadSvg)
onMounted(loadSvg)

defineExpose({
  getSVGContainer: () => svgContainer.value,
})
</script>

<style scoped lang="scss">
.m-svg-wrapper { width: 100%; }
.m-svg-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  :deep(svg) { max-width: 100%; height: auto; }
  &--disabled { opacity: 0.6; pointer-events: none; }
}
.m-svg-hint { font-size: 0.75rem; color: #6b7280; margin-top: 0.5rem; }
</style>
